<script setup>
import AppButton from "@/Components/AppButton.vue";
import BootstrapModal from "@/Components/BootstrapModal.vue";
import FlatTextarea from "@/Components/FlatTextarea.vue";
import RatingInput from "@/Components/RatingInput.vue";
import { ref } from "vue";

const modalRef = ref(null);
const rating = ref(0);
const feedbackText = ref("");

// message state inside modal
const messageText = ref("");
const messageType = ref("success"); // 'success' | 'error'
let messageTimer = null;

const emit = defineEmits(["submitted"]);

defineProps({
    loading: { type: Boolean, default: false },
    secondaryLink: { type: String, default: false },
});

function submit() {
    if (!rating.value || !feedbackText.value.trim()) return;
    // emit up to parent; parent will flip its `loading` prop on/off
    emit("submitted", {
        rating: rating.value,
        feedback: feedbackText.value.trim(),
    });

    // Clear your form state
    feedbackText.value = ""; // blanks the textarea
    rating.value = 0; // deselects all emojis
}

// Allow parent to display a message inside the modal
function showMessage(text, type = "success", timeout = 3000) {
    if (messageTimer) {
        clearTimeout(messageTimer);
        messageTimer = null;
    }
    messageText.value = text;
    messageType.value = type;
    if (timeout) {
        messageTimer = setTimeout(() => {
            messageText.value = "";
            messageTimer = null;
        }, timeout);
    }
}

// **SIMPLIFIED** open/close
function open() {
    modalRef.value?.showModal("feedback-modal");
}
function close() {
    modalRef.value?.closeModal("feedback-modal");
}

defineExpose({ open, close, showMessage });
</script>

<template>
    <BootstrapModal
        ref="modalRef"
        modalId="feedback-modal"
        title="We'd love your feedback"
        modalSize="m"
        closeText="Close"
    >
        <template #body>
            <div class="px-4 py-3 text-center">
                <!-- inline message -->
                <div
                    v-if="messageText"
                    :class="['inline-msg', messageType]"
                    class="mb-3"
                >
                    {{ messageText }}
                </div>

                <RatingInput v-model="rating" mode="emoji" class="mb-4" />

                <label style="font-weight: bolder" class="mb-3"
                    >Anything we can do better?</label
                >
                <FlatTextarea
                    v-model="feedbackText"
                    placeholder="Feedback, praise, bugs — we're all ears!"
                    rows="4"
                />
                <div class="mt-3">
                    <AppButton
                        width="100%"
                        :loading="loading"
                        :disabled="!rating"
                        @buttonClick="submit"
                        :ignore-plan-restriction="true"
                    >
                        Send feedback
                    </AppButton>
                </div>
            </div>
        </template>
        <!-- FOOTER SLOT -->
        <template #footer>
            <div class="w-100 text-center py-2" v-if="false">
                <a
                    href="/schedule-call"
                    target="_blank"
                    class="small text-gray text-decoration-none"
                >
                    Interested in sharing more? Schedule a call now
                </a>
            </div>
        </template>
    </BootstrapModal>
</template>

<style scoped>
/* simple inline message styles */
.inline-msg {
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 14px;
}
.inline-msg.success {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
}
.inline-msg.error {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
}
</style>
